import {
  ClockCircleOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  DownOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Input,
  Menu,
  notification,
  Row,
  Select,
  Space,
  Switch,
  TimeRangePickerProps,
  Tooltip,
} from 'antd';
import { useAuthConnectionEffect } from 'hooks/useAuthConnectionEffect';
import { ManualPatchPayload, PatchDriverPayload } from 'interfaces';
import moment from 'moment';
import * as R from 'ramda';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as CompaniesSlice from 'slices/companies';
import { useCompanies } from 'slices/companies';
import { useDrivers } from 'slices/drivers';
import {
  RemoveManualPatchDriverPayload,
  UpdateManualPatchDriverPayload,
  UpdateManualPatchPayload,
} from 'slices/manualPatch';
import { ResponsePayload } from 'slices/subscriptive';
import { fuzzySelectFilter, getContainer } from 'utils/html';
import { useCopyToClipboard } from '../hooks/use-copy-to-clipboard';
import { useDebounceCallback } from '../hooks/use-debounce-callback';
import { getDriverTimezone } from '../utils/driver';
import { timezones } from '../utils/timezone';
import type { ProfileFormData } from './ProfileForm';

export default function SelectStep(props: {
  onlyLast8Days: boolean;
  patch: ManualPatchPayload;
  updatePatch: (patch: UpdateManualPatchPayload, reset?: boolean) => Promise<ResponsePayload>;
  updatePatchDriver: (driver: UpdateManualPatchDriverPayload) => Promise<ResponsePayload>;
  removePatchDriver: (driver: RemoveManualPatchDriverPayload) => Promise<ResponsePayload>;
  disabled: boolean | undefined;
  fetchEvents: (driverId: string) => void;
  onAvailableHrsClick: () => void;
  onDownloadReportClick: (driverId: string) => void;
  onProfileForm: (data: ProfileFormData) => void;
}) {
  const dispatch = useDispatch();
  const {
    updatePatch,
    updatePatchDriver,
    removePatchDriver,
    patch,
    disabled,
    fetchEvents,
    onAvailableHrsClick,
    onDownloadReportClick,
    onProfileForm,
  } = props;
  const { companies, companiesSubscribed, companiesLoading } = useCompanies();
  const { drivers, driversSubscribed, driversLoading } = useDrivers(patch.companyId || null);
  const [
    ,
    copy,
  ] = useCopyToClipboard();
  const handleCopy = async (text: string) => {
    try {
      await copy(text);
      notification.success({
        message: 'Copied!',
      });
    } catch (error) {
      notification.error({
        message: 'Failed to copy!',
      });
    }
  };
  const [
    mainDriverDateRange,
    setMainDriverDateRange,
  ] = useState<{ from: string | null; to: string | null }>({
    from: patch?.from ?? null,
    to: patch?.to ?? null,
  });

  useAuthConnectionEffect(() => {
    dispatch(CompaniesSlice.subscribe());
    return () => {
      dispatch(CompaniesSlice.unsubscribe());
    };
  }, []);

  const patchDrivers = useMemo(() => {
    return [...patch.drivers].sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());
  }, [patch.drivers]);

  const [
    hasCoDriver,
    setHasCoDriver,
  ] = useState(() => patchDrivers.length > 1);

  const datePickerRanges = useMemo(() => {
    const ranges: Record<string, TimeRangePickerProps['ranges']> = {};
    if ((companies ?? []).length === 0 || (drivers ?? []).length === 0) {
      return ranges;
    }

    const company = companies.find((company) => company._id === patch.companyId);
    if (!company) {
      return ranges;
    }

    for (const driverId of patchDrivers.map((driver) => driver.driverId)) {
      const driver = drivers?.find((driver) => driver._id === driverId);
      if (driver) {
        const driverTimezone = getDriverTimezone(company, driver);
        ranges[driverId] = {
          'Last 9 days': [
            moment.tz(timezones[driverTimezone]).subtract(9, 'days'),
            moment.tz(timezones[driverTimezone]),
          ],
          'Last 16 days': [
            moment.tz(timezones[driverTimezone]).subtract(16, 'days'),
            moment.tz(timezones[driverTimezone]),
          ],
          'Last 30 days': [
            moment.tz(timezones[driverTimezone]).subtract(30, 'days'),
            moment.tz(timezones[driverTimezone]),
          ],
        };
      }
    }

    return ranges;
  }, [
    JSON.stringify(patch.drivers),
    patch.companyId,
    companies,
    drivers,
  ]);

  // const datePickerRanges;

  const driverLogUrls = useMemo(() => {
    const hosPortalUrl = process.env.REACT_APP_HOS_PORTAL_URL;
    if (
      !hosPortalUrl ||
      companies.length === 0 ||
      !drivers ||
      drivers?.length === 0 ||
      !patch.companyId ||
      patch.drivers.length === 0
    ) {
      return {} as Record<string, string>;
    }

    const selectedCompany = companies.find((company) => company._id === patch.companyId);
    if (selectedCompany === undefined) {
      return {} as Record<string, string>;
    }

    return patch.drivers.reduce(
      (acc, patchDriver) => {
        const driverTimezone = getDriverTimezone(
          selectedCompany,
          drivers.find((driver) => driver._id === patchDriver.driverId)
        );

        acc[patchDriver.driverId] = `${hosPortalUrl}/portal/log/${patchDriver.driverId}/${moment
          .tz(timezones[driverTimezone])
          .format('M-D-YYYY')}/0/${driverTimezone}`;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [
    patch.companyId,
    patch.drivers,
    drivers,
    companies,
  ]);

  const onClick = useDebounceCallback(
    (driverId: string) => {
      fetchEvents(driverId);
    },
    1000,
    {
      leading: true,
      trailing: false,
    }
  );

  const eightDaysAgo = moment().subtract(8, 'days');

  const driverSelectorRow = (key: string, patchDriver?: PatchDriverPayload, isFirstRow = false) =>
    isFirstRow || (!isFirstRow && hasCoDriver) ? (
      <Row
        key={key}
        justify="start"
        gutter={[
          8,
          8,
        ]}
        style={{ marginBottom: '8px' }}
      >
        {isFirstRow ? (
          <>
            <Col span={2} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Switch
                checkedChildren="- CoDriver"
                unCheckedChildren="+ CoDriver"
                checked={hasCoDriver}
                onChange={(checked) => {
                  if (!checked && patchDrivers.length > 1) {
                    const coDriver = patchDrivers[1];
                    // noinspection JSIgnoredPromiseFromCall
                    removePatchDriver({
                      patchId: coDriver.patchId,
                      driverId: coDriver.driverId,
                    });
                  }
                  setHasCoDriver(checked);
                }}
              />
            </Col>
            <Col span={4}>
              <Input.Group compact>
                <Tooltip getPopupContainer={getContainer} getTooltipContainer={getContainer} title="Select company">
                  <Select<string, { label: string; value: string }>
                    placeholder="Select company"
                    showSearch
                    getPopupContainer={getContainer}
                    loading={companiesLoading}
                    disabled={disabled || !companiesSubscribed}
                    options={companies.map((company) => ({
                      label: company.name,
                      value: company._id,
                    }))}
                    value={patch.companyId || undefined}
                    onChange={(companyId) => {
                      companyId !== patch.companyId &&
                        updatePatch(
                          R.pipe<ManualPatchPayload, ManualPatchPayload, ManualPatchPayload>(
                            R.assoc('companyId', companyId),
                            R.assoc('driverId', null)
                          )(patch)
                        );
                    }}
                    filterOption={fuzzySelectFilter}
                    style={{ width: 'calc(100% - 35px)' }}
                  />
                </Tooltip>
                <Tooltip title="Copy company name">
                  <Button
                    disabled={!patch.companyId}
                    icon={<CopyOutlined />}
                    onClick={async () => {
                      const company = companies.find((company) => company._id === patch.companyId);
                      if (!company) {
                        return;
                      }

                      await handleCopy(company.name);
                    }}
                  />
                </Tooltip>
              </Input.Group>
            </Col>
          </>
        ) : null}
        <Col span={6} offset={!isFirstRow ? 6 : 0}>
          <Input.Group compact>
            <Tooltip getPopupContainer={getContainer} getTooltipContainer={getContainer} title="Select driver">
              <Select<string | undefined, { label: string; value: string }>
                placeholder="Select driver"
                showSearch
                getPopupContainer={getContainer}
                loading={driversLoading}
                disabled={
                  disabled ||
                  !driversSubscribed ||
                  (patchDriver?.driverId !== undefined &&
                    drivers?.find((driver) => driver._id === patchDriver?.driverId) === undefined)
                }
                allowClear={patchDriver && !isFirstRow}
                options={drivers
                  ?.filter(
                    (driver) =>
                      driver._id === patchDriver?.driverId ||
                      !patch.drivers.map((patchDriver) => patchDriver.driverId).includes(driver._id)
                  )
                  .map((driver) => ({
                    label: `${driver.firstName} ${driver.lastName}`,
                    value: driver._id,
                  }))}
                value={patchDriver?.driverId}
                onChange={(driverId) => {
                  if (!driverId || !patch.companyId) {
                    return;
                  }

                  if (patchDriver?.driverId === undefined) {
                    let from = mainDriverDateRange.from;
                    let to = mainDriverDateRange.to;
                    if (isFirstRow) {
                      const driverTimezone = getDriverTimezone(
                        companies.find((company) => company._id === patch.companyId)!,
                        drivers?.find((driver) => driver._id === driverId)
                      );
                      from = moment.tz(timezones[driverTimezone]).subtract(9, 'days').toISOString();
                      to = moment.tz(timezones[driverTimezone]).toISOString();
                      setMainDriverDateRange({
                        from,
                        to,
                      });
                    }
                    // noinspection JSIgnoredPromiseFromCall
                    updatePatch({
                      ...patch,
                      driverId,
                      from,
                      to,
                    });
                  } else if (patchDriver.driverId !== driverId) {
                    // noinspection JSIgnoredPromiseFromCall
                    updatePatchDriver({
                      patchId: patch.id,
                      driverId: patchDriver.driverId,
                      updatedDriverId: driverId,
                      from: mainDriverDateRange.from,
                      to: mainDriverDateRange.to,
                    });
                  }
                }}
                onClear={() => {
                  if (patchDriver) {
                    // noinspection JSIgnoredPromiseFromCall
                    removePatchDriver({
                      patchId: patchDriver.patchId,
                      driverId: patchDriver.driverId,
                    });
                  }
                }}
                filterOption={fuzzySelectFilter}
                style={{ width: 'calc(100% - 35px)' }}
              />
            </Tooltip>
            <Tooltip title="Copy driver name">
              <Button
                disabled={!patchDriver}
                icon={<CopyOutlined />}
                onClick={async () => {
                  if (!drivers) {
                    return;
                  }
                  const driver = drivers.find((driver) => driver._id === patchDriver?.driverId);
                  if (!driver) {
                    return;
                  }

                  await handleCopy(`${driver.firstName} ${driver.lastName}`);
                }}
              />
            </Tooltip>
          </Input.Group>
        </Col>
        <Col span={5}>
          <DatePicker.RangePicker
            ranges={patchDriver?.driverId ? datePickerRanges?.[patchDriver.driverId] : undefined}
            disabledDate={(date) => (props.onlyLast8Days ? eightDaysAgo.isAfter(date) : false)}
            disabled={disabled || !patchDriver}
            allowClear={false}
            value={[
              patchDriver?.from ? moment.utc(patchDriver.from) : null,
              patchDriver?.to ? moment.utc(patchDriver.to) : null,
            ]}
            format="MMM DD, YYYY"
            onChange={(range) => {
              if (range) {
                if (isFirstRow) {
                  let from = null;
                  let to = null;
                  if (range[0]) {
                    from = moment.utc(range[0].format('YYYY-MM-DD')).toISOString();
                  }
                  if (range[1]) {
                    to = moment.utc(range[1].format('YYYY-MM-DD'))?.toISOString();
                  }
                  setMainDriverDateRange({
                    from,
                    to,
                  });
                }
                if (patchDriver) {
                  // noinspection JSIgnoredPromiseFromCall
                  updatePatchDriver({
                    patchId: patch.id,
                    driverId: patchDriver.driverId,
                    from: range[0] ? moment.utc(range[0].format('YYYY-MM-DD'))?.toISOString() : null,
                    to: range[1] ? moment.utc(range[1].format('YYYY-MM-DD'))?.toISOString() : null,
                  });
                } else {
                  // noinspection JSIgnoredPromiseFromCall
                  updatePatch({
                    ...patch,
                    from: range[0] ? moment.utc(range[0].format('YYYY-MM-DD'))?.toISOString() : null,
                    to: range[1] ? moment.utc(range[1].format('YYYY-MM-DD'))?.toISOString() : null,
                  });
                }
              }
            }}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={3}>
          {patchDriver && driverLogUrls[patchDriver.driverId] ? (
            <Dropdown.Button
              disabled={disabled || !(patchDriver?.driverId && patchDriver?.from && patchDriver?.to)}
              type="primary"
              htmlType="submit"
              icon={<DownOutlined />}
              onClick={() => onClick(patchDriver.driverId)}
              className="grow-child"
              overlay={
                <Menu>
                  <Menu.Item key="hos-portal" disabled={!patchDriver}>
                    <Tooltip title={`Please log in to HOS portal of ${patch.companyName} before using`}>
                      <a
                        href={driverLogUrls[patchDriver!.driverId]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          paddingTop: 0,
                          lineHeight: '30px',
                        }}
                        title="Go to driver's log"
                      >
                        HOS Portal
                      </a>
                    </Tooltip>
                  </Menu.Item>
                </Menu>
              }
            >
              LOAD
            </Dropdown.Button>
          ) : (
            <Button
              disabled={disabled || !(patchDriver?.driverId && patchDriver?.from && patchDriver?.to)}
              type="primary"
              htmlType="submit"
              onClick={() => patchDriver && onClick(patchDriver.driverId)}
              className="grow-child"
            >
              LOAD
            </Button>
          )}
        </Col>
        <Col span={4}>
          <Space>
            <Tooltip title="Download Report">
              <Button
                icon={<CloudDownloadOutlined />}
                onClick={() => patchDriver?.driverId && onDownloadReportClick(patchDriver.driverId)}
                disabled={disabled || !(patchDriver?.driverId && patchDriver?.from && patchDriver?.to)}
              />
            </Tooltip>
            <Tooltip title="Profile Form">
              <Button
                icon={<UserOutlined />}
                onClick={() => {
                  onProfileForm({
                    driverId: patchDriver?.driverId,
                    companyId: patch.companyId,
                    from: patchDriver?.from,
                    to: patchDriver?.to,
                  });
                }}
                disabled={disabled || !(patchDriver?.driverId && patchDriver?.from && patchDriver?.to)}
              />
            </Tooltip>
            {isFirstRow ? (
              <Tooltip title="Available Hours">
                <Button
                  icon={<ClockCircleOutlined />}
                  onClick={onAvailableHrsClick}
                  disabled={disabled || !(patchDriver?.driverId && patchDriver?.from && patchDriver?.to)}
                />
              </Tooltip>
            ) : null}
          </Space>
        </Col>
      </Row>
    ) : null;

  return (
    <>
      <Row>
        <Col span={24}>
          {[
            ...patchDrivers,
            ...[undefined].filter((val) => (patchDrivers.length === 2 ? val !== undefined : true)),
          ].map((driver, index) => driverSelectorRow(`driver-${index}`, driver, index === 0))}
        </Col>
      </Row>
    </>
  );
}
